import React, { useState } from "react";
import { toast } from "sonner";
import {
  MessageSquare, CheckCircle2, XCircle, Clock, AlertTriangle, RefreshCw, Send,
} from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { fmtDate, fmtEUR } from "@/constants/labels";
import { tierFor } from "@/lib/echeancierRules";

/**
 * Thread de négociation « contre-offre » attaché à une candidature.
 *
 * Props :
 *  - candidature : objet enrichi par ``_serialize_contre_offres`` côté serveur
 *    (contre_offres, contre_offre_pending, contre_offre_closed,
 *     contre_offre_rounds, contre_offre_pending_confirmation).
 *  - role : "entreprise" | "prestataire".
 *  - currentUserId : id user connecté (pour repérer les confirmations).
 *  - onChanged : callback à appeler après une action pour recharger.
 *
 * L'auteur alterne entre les 2 parties. Max 3 propositions par côté puis
 * clôture. Chaque offre expire à ``expires_at`` (48h ouvrées côté serveur).
 */
export default function ContreOffreThread({ candidature: c, role, currentUserId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [propose, setPropose] = useState({ open: false, montant: "", motif: "" });
  const [refuse, setRefuse] = useState({ open: false, motif: "" });

  const offres = c?.contre_offres || [];
  const pending = c?.contre_offre_pending;
  const closed = !!c?.contre_offre_closed;
  const preview = c?.contre_offre_pending_confirmation;
  const rounds = c?.contre_offre_rounds || { entreprise: 0, prestataire: 0 };
  const myRounds = rounds[role] || 0;
  const maxRounds = 3;
  const remaining = Math.max(0, maxRounds - myRounds);

  // Peut-on proposer maintenant ?
  const canPropose = !closed && !preview
    && (!pending || pending.from_role !== role)
    && myRounds < maxRounds;
  // Peut-on décider ?
  const canDecide = !closed && !preview && pending && pending.from_role !== role;
  // Peut-on confirmer l'aperçu (dual confirmation) ?
  const iAlreadyConfirmed = preview?.confirmed_by?.includes(currentUserId);
  const canConfirm = !closed && preview && !iAlreadyConfirmed;

  const submitPropose = async () => {
    const montant = Number(propose.montant);
    if (!(montant > 0)) return toast.error("Montant HT invalide");
    if ((propose.motif || "").trim().length < 5) return toast.error("Motif obligatoire (min 5 caractères)");
    setBusy(true);
    try {
      await api.post(`/candidatures/${c.id}/contre-offre`,
        { montant_ht: montant, motif: propose.motif.trim() });
      toast.success("Contre-offre envoyée — l'autre partie a 48h ouvrées pour répondre");
      setPropose({ open: false, montant: "", motif: "" });
      onChanged?.();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (decision) => {
    if (decision === "refusee" && (refuse.motif || "").trim().length > 0 && refuse.motif.length < 5) {
      return toast.error("Motif de refus : au moins 5 caractères (ou laissez vide)");
    }
    setBusy(true);
    try {
      const body = { decision };
      if (decision === "refusee" && refuse.motif) body.motif = refuse.motif;
      const { data } = await api.post(`/candidatures/${c.id}/contre-offre/decider`, body);
      if (data.decision === "acceptee_appliquee") {
        toast.success(`Contre-offre acceptée — nouveau prix ${fmtEUR(data.new_montant_ht)} appliqué`);
      } else if (data.decision === "acceptee_en_confirmation") {
        toast.success("Aperçu généré — les deux parties doivent confirmer le nouvel échéancier (changement de palier)");
      } else {
        toast.success("Contre-offre refusée");
      }
      setRefuse({ open: false, motif: "" });
      onChanged?.();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  const confirmPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/candidatures/${c.id}/contre-offre/confirmer`);
      toast.success(data.finalized
        ? "Aperçu confirmé par les deux parties — nouveau prix officialisé"
        : "Confirmation enregistrée — en attente de l'autre partie");
      onChanged?.();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E4E7EB] rounded-[6px] p-3 bg-[#F7F8FA] mt-2" data-testid={`contre-offre-thread-${c.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1F2933]">
          <MessageSquare size={14} className="text-[#1B3A5C]" />
          Négociation ({offres.length} offre{offres.length > 1 ? "s" : ""})
          <span className="text-xs font-normal text-[#3E4C59]">— {remaining} proposition(s) restante(s) de votre côté</span>
        </div>
        {c.montant_ht_negocie && (
          <span className="badge-valid" data-testid={`contre-offre-final-${c.id}`}>
            <CheckCircle2 size={12} /> Prix négocié : {fmtEUR(c.montant_ht_negocie)}
          </span>
        )}
        {closed && (
          <span className="badge-muted" data-testid={`contre-offre-closed-${c.id}`}>
            <XCircle size={12} /> Négociation clôturée ({c.contre_offre_close_reason === "max_rounds" ? "limite atteinte" : "clôturée"})
          </span>
        )}
      </div>

      {/* Aperçu dual-confirmation en cours (changement de palier) */}
      {preview && (
        <div className="border-l-4 border-[#E8710A] bg-[#FEF5EA] p-3 text-[13px] mb-2" data-testid={`contre-offre-preview-${c.id}`}>
          <p className="font-semibold text-[#1F2933] mb-1 inline-flex items-center gap-1">
            <AlertTriangle size={14} /> Aperçu à confirmer — changement de palier
          </p>
          <p>Nouveau montant : <b>{fmtEUR(preview.montant_ht)}</b> · Ancien : {fmtEUR(preview.old_montant_ht)}</p>
          <p className="text-xs text-[#3E4C59] mb-1">
            Palier {preview.old_tier} → <b>{preview.new_tier}</b> — l'échéancier a été recalculé.
            Les DEUX parties doivent confirmer pour officialiser le nouveau prix.
          </p>
          <ul className="mt-1 border border-[#F0D9B5] rounded-[4px] bg-white p-2 space-y-0.5 text-[12px]">
            {(preview.echeancier || []).map((l, i) => (
              <li key={i} className="flex justify-between">
                <span>{l.label} <span className="text-[#3E4C59]">({l.type})</span></span>
                <b>{fmtEUR(l.montant_ht)}</b>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-xs text-[#3E4C59]">
            Confirmé par {preview.confirmed_by?.length || 0} / 2 partie(s).
          </div>
          {canConfirm && (
            <button onClick={confirmPreview} disabled={busy}
                    className="btn-primary text-xs mt-2" data-testid={`contre-offre-confirm-${c.id}`}>
              <CheckCircle2 size={12} /> Confirmer le nouvel échéancier
            </button>
          )}
        </div>
      )}

      {/* Historique */}
      {offres.length > 0 && (
        <ul className="space-y-2 mb-2">
          {offres.map((o) => (
            <li key={o.id} className="border border-[#E4E7EB] rounded-[4px] p-2 bg-white text-[13px]"
                data-testid={`contre-offre-item-${o.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-1">
                <div>
                  <b>{fmtEUR(o.montant_ht)}</b> HT
                  <span className="text-xs text-[#3E4C59] ml-2">
                    proposé par le {o.from_role === "prestataire" ? "prestataire" : "donneur d'ordre"} · {fmtDate(o.at)}
                  </span>
                </div>
                <OfferStatusBadge statut={o.statut} expiresAt={o.expires_at} />
              </div>
              <div className="italic text-[#1F2933] mt-1">« {o.motif} »</div>
              {o.refus_motif && (
                <div className="text-xs text-[#C0392B] mt-1">Motif refus : {o.refus_motif}</div>
              )}
              {o.statut === "en_attente" && o.expires_at && (
                <div className="text-xs text-[#3E4C59] mt-1 inline-flex items-center gap-1">
                  <Clock size={10} /> Expire le {fmtDate(o.expires_at)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Actions */}
      <div className="flex gap-1 flex-wrap">
        {canDecide && (
          <>
            <button onClick={() => decide("acceptee")} disabled={busy}
                    className="btn-primary text-xs" data-testid={`contre-offre-accept-${c.id}`}>
              <CheckCircle2 size={12} /> Accepter {fmtEUR(pending.montant_ht)}
            </button>
            <button onClick={() => setRefuse({ open: true, motif: "" })} disabled={busy}
                    className="btn-danger text-xs" data-testid={`contre-offre-refuse-${c.id}`}>
              <XCircle size={12} /> Refuser
            </button>
          </>
        )}
        {canPropose && (
          <button onClick={() => setPropose({ open: true, montant: "", motif: "" })}
                  disabled={busy}
                  className="btn-outline text-xs"
                  data-testid={`contre-offre-propose-${c.id}`}>
            <RefreshCw size={12} /> {offres.length === 0 ? "Proposer une contre-offre" : "Contre-contre-offre"}
          </button>
        )}
      </div>

      {/* Modales */}
      {propose.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 overflow-y-auto">
          <div className="bg-white w-full max-w-lg rounded-[6px] border border-[#E4E7EB] shadow-lg my-4">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E7EB] bg-[#F5F6F7]">
              <h2 className="text-lg font-semibold text-[#1F2933]">Proposer une contre-offre</h2>
              <button onClick={() => setPropose({ open: false, montant: "", motif: "" })} className="btn-ghost text-sm">✕</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <p className="text-[#3E4C59]">L'autre partie aura 48 heures ouvrées pour répondre. Rappel automatique à J+1 ouvré.</p>
              <div>
                <label className="label">Nouveau montant HT (€) <span className="text-[#C0392B]">*</span></label>
                <input type="number" min="0.01" step="0.01" className="input"
                       value={propose.montant}
                       onChange={(e) => setPropose({ ...propose, montant: e.target.value })}
                       data-testid={`contre-offre-montant-input-${c.id}`} />
                {Number(propose.montant) > 0 && (
                  <div className="text-xs text-[#3E4C59] mt-1">
                    Palier détecté : <b>{tierFor(Number(propose.montant)) === 1 ? "< 3 000 €" :
                      tierFor(Number(propose.montant)) === 2 ? "3 000-6 000 €" : "> 6 000 €"}</b>
                  </div>
                )}
              </div>
              <div>
                <label className="label">Motif écrit <span className="text-[#C0392B]">*</span></label>
                <textarea rows={4} minLength={5} maxLength={1000} className="textarea"
                          placeholder="Ex : le CCTP décrit plus de mètres linéaires que la visite terrain, matériaux à ajuster…"
                          value={propose.motif}
                          onChange={(e) => setPropose({ ...propose, motif: e.target.value })}
                          data-testid={`contre-offre-motif-input-${c.id}`} />
                <div className="text-xs text-[#3E4C59]">{propose.motif.length} / 1000 — min 5 caractères</div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
                <button onClick={() => setPropose({ open: false, montant: "", motif: "" })} className="btn-ghost text-sm">Annuler</button>
                <button onClick={submitPropose} disabled={busy}
                        className="btn-primary" data-testid={`contre-offre-submit-${c.id}`}>
                  <Send size={14} /> {busy ? "…" : "Envoyer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {refuse.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 overflow-y-auto">
          <div className="bg-white w-full max-w-lg rounded-[6px] border border-[#E4E7EB] shadow-lg my-4">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E7EB] bg-[#F5F6F7]">
              <h2 className="text-lg font-semibold text-[#1F2933]">Refuser la contre-offre</h2>
              <button onClick={() => setRefuse({ open: false, motif: "" })} className="btn-ghost text-sm">✕</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div>
                <label className="label">Motif du refus (optionnel)</label>
                <textarea rows={3} maxLength={1000} className="textarea"
                          value={refuse.motif}
                          onChange={(e) => setRefuse({ ...refuse, motif: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
                <button onClick={() => setRefuse({ open: false, motif: "" })} className="btn-ghost text-sm">Annuler</button>
                <button onClick={() => decide("refusee")} disabled={busy}
                        className="btn-danger" data-testid={`contre-offre-refuse-confirm-${c.id}`}>
                  <XCircle size={14} /> Confirmer le refus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function OfferStatusBadge({ statut, expiresAt }) {
  const map = {
    en_attente: { cls: "badge-warn", icon: <Clock size={10} />, label: "En attente" },
    acceptee_appliquee: { cls: "badge-valid", icon: <CheckCircle2 size={10} />, label: "Acceptée & appliquée" },
    acceptee_en_confirmation: { cls: "badge-warn", icon: <AlertTriangle size={10} />, label: "Aperçu à confirmer" },
    refusee: { cls: "badge-danger", icon: <XCircle size={10} />, label: "Refusée" },
    surenchere: { cls: "badge-muted", icon: <RefreshCw size={10} />, label: "Superseded" },
    expiree: { cls: "badge-muted", icon: <Clock size={10} />, label: "Expirée" },
  };
  const s = map[statut] || map.en_attente;
  return <span className={s.cls}>{s.icon} {s.label}</span>;
}
